import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Skeleton, useTheme } from "@mui/material";
import * as React from "react";

import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { EventRecord } from "@/shared/domain/events";

import { RobotoTypography } from "../../RobotoTypography";
import {
  useWorkspaceEvent,
  useUpdateWorkspaceEvent,
} from "../WorkspaceCtx/useWorkspaceEvents";

import styles from "./EventDescription.module.css";
import { EventDescriptionForm } from "./EventDescriptionForm";

interface EventDescriptionProps {
  eventId: EventRecord["event_id"];
}

export function EventDescription({ eventId }: EventDescriptionProps) {
  const eventQuery = useWorkspaceEvent(eventId);
  const updateEventMutation = useUpdateWorkspaceEvent();
  const theme = useTheme();

  const [isEditing, setIsEditing] = React.useState<boolean>(false);

  if (eventQuery.isPending) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Description
        </RobotoTypography>
        <div className={styles.sectionPadding}>
          <Skeleton variant="rounded" height="40px" />
        </div>
      </section>
    );
  }

  if (eventQuery.isError) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Description
        </RobotoTypography>
        <div className={styles.errorContainer}>
          <ErrorOutlineIcon color="error" />
          <RobotoTypography>Failed to load event description</RobotoTypography>
        </div>
      </section>
    );
  }

  const { description } = eventQuery.data;

  if (isEditing) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Description
        </RobotoTypography>
        <div>
          <EventDescriptionForm
            description={description}
            onSubmit={async (description) => {
              await updateEventMutation.mutateAsync({
                eventId,
                request: {
                  description,
                },
              });
              setIsEditing(false);
            }}
            onCancel={() => setIsEditing(false)}
            inputClassName={styles.description}
          />
        </div>
      </section>
    );
  }

  return (
    <section>
      <RobotoTypography component={"h3"} className={styles.title}>
        Description
      </RobotoTypography>
      <Box
        onClick={() => setIsEditing(true)}
        sx={{
          ":hover": {
            backgroundColor: theme.palette.hoverBackground.main,
            borderRadius: "0.25rem",
            cursor: "pointer",
          },
        }}
      >
        {description ? (
          <RobotoMarkdown
            content={description}
            className={styles.description}
          />
        ) : (
          <RobotoTypography component="p" className={styles.description}>
            {"Add a description..."}
          </RobotoTypography>
        )}
      </Box>
    </section>
  );
}
